/**
 * Confirm Dialog Component
 */
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmText?: string; // Alias for confirmLabel
  cancelText?: string; // Alias for cancelLabel
  confirmColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  onConfirm: () => void;
  onCancel?: () => void; // Make optional
  onClose?: () => void; // Support onClose as well
  loading?: boolean;
}

const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  confirmText,
  cancelText,
  confirmColor = 'primary',
  onConfirm,
  onCancel,
  onClose,
  loading = false,
}: ConfirmDialogProps) => {
  const { t } = useTranslation();
  // Use onClose if provided, otherwise use onCancel
  const handleCancel = onClose || onCancel || (() => {});

  // Use text props if provided, otherwise use label props
  const finalConfirmLabel = confirmText || confirmLabel || t('common.confirm');
  const finalCancelLabel = cancelText || cancelLabel || t('common.cancel');

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleCancel} disabled={loading}>
          {finalCancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={confirmColor}
          disabled={loading}
        >
          {loading ? t('common.loading') : finalConfirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;

